import React from "react";
import { isEqual } from "lodash";
import { GridColumnVisibilityModel, GridPinnedColumns, GridRowSelectionModel, GRID_ROOT_GROUP_ID, } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import tableHelper from "src/core/tables/tableHelper";
import useUpdateEffect from "src/shared/helpers/useUpdateEffect";
import { useSettingsContext } from "src/core/settings/useSettings";
import { getNestedValue } from "src/core/settings/settings";
import { EMPTY_VISIBILITY, getTableViewState, resolveColumnVisibility, snapshotColumnVisibility } from "./tableViewState";
import { TableDialogHandle, TableDialogPayload, } from "src/core/tables/helpers/TableDialog";

export const normalizeFlatToNested = (row: any) => {
  if (!row) return row;
  const next = { ...row };
  Object.keys(row).forEach((field) => {
    const underscore = field.indexOf("_");
    if (underscore > 0) {
      const base = field.slice(0, underscore);
      const nested = field.slice(underscore + 1);
      next[base] = { ...(row[base] ?? {}), [nested]: row[field] };
    }
  });
  return next;
};

export const useTableVisibility = (columns: any[] = [], tableName = "UnnamedTable", defaultColumnVisibilityModel: GridColumnVisibilityModel = EMPTY_VISIBILITY) => {
  const [pinnedColumns, setPinnedColumns] = React.useState<GridPinnedColumns>();
  const { settings, setSetting } = useSettingsContext();
  const { activeViewId, activeView } = getTableViewState(settings, tableName);
  const columnVisibilityModel = resolveColumnVisibility(activeView.Active, defaultColumnVisibilityModel);
  const tableExpanded = getNestedValue(settings, ["TableExpanded", tableName], false,);
  const updateColumnVisibilityModel = React.useCallback(
    (newModel: GridColumnVisibilityModel) => {
      if (isEqual(newModel, columnVisibilityModel)) return;
      setSetting(["TableColumnVisibility", tableName, activeViewId, "Active"], snapshotColumnVisibility(columns, newModel),);
    },
    [setSetting, tableName, activeViewId, columns, columnVisibilityModel],
  );
  const handlePinnedColumnsChange = React.useCallback((updatedPinnedColumns: GridPinnedColumns) => { setPinnedColumns(updatedPinnedColumns); }, [],);
  return { pinnedColumns, setPinnedColumns, handlePinnedColumnsChange, columnVisibilityModel, updateColumnVisibilityModel, tableExpanded, };
};

export const useRowsLoadingFlag = (allRows: any, setLoading?: (value: boolean) => void,) => {
  useUpdateEffect(() => {
    if (!setLoading) return;
    setLoading(false);
  }, [allRows]);
};

export const useInitialUrlFilter = (initialTableSettings: HasFilterAndApiRef,) => {
  const initalFilter = tableHelper.getDataGridUrlFilter();
  useUpdateEffect(() => {
    if (!initalFilter || !initalFilter.field) return;
    const { filterModel, apiRef } = initialTableSettings;
    if (filterModel && filterModel.items?.length > 0) return;
    if (!apiRef?.current) return;
    const column = apiRef.current.getAllColumns().find((c: any) => c.field === initalFilter.field);
    if (!column) return;
    let filterToApply = initalFilter;
    if (column.type === "number") {
      filterToApply = { ...initalFilter, operator: "=", };
    }
    apiRef.current.setFilterModel({ items: [filterToApply], });
  }, [initalFilter, initialTableSettings]);
  return initalFilter;
};

export const useRowSelectionHandler = (selectOnlyOneRow: boolean | undefined, rowSelectionModel: GridRowSelectionModel, setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>, removeMultiEditRow?: () => void,) => {
  return React.useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      if (selectOnlyOneRow) {
        if (newSelectionModel?.length > 1) {
          const selectionSet = new Set(rowSelectionModel);
          const result = newSelectionModel.filter((s) => !selectionSet.has(s),);
          setRowSelectionModel(result);
        } else {
          setRowSelectionModel(newSelectionModel);
        }
      } else {
        setRowSelectionModel(newSelectionModel);
        if (!newSelectionModel || newSelectionModel.length <= 1) {
          removeMultiEditRow?.();
        }
        // original commented multi-edit row code intentionally omitted
      }
    },
    [selectOnlyOneRow, rowSelectionModel, setRowSelectionModel, removeMultiEditRow],
  );
};

export const useTableDialog = () => {
  const dialogRef = React.useRef<TableDialogHandle | null>(null);
  const open = React.useCallback((p: TableDialogPayload) => { dialogRef.current?.open(p); }, []);
  const update = React.useCallback((p: Partial<TableDialogPayload>) => { dialogRef.current?.update(p); }, []);
  const close = React.useCallback(() => { dialogRef.current?.close(); }, []);
  return { ref: dialogRef, open, update, close, };
};

export const useProcessRowUpdateRef = (idPath: string[] | undefined, tableName: string | undefined, changedRowsRef: React.MutableRefObject<any[]>, apiRef: React.MutableRefObject<GridApiPremium | undefined>, setSetting: (path: string[], value: any) => void,) => {
  const processRowUpdateRef = React.useRef<(newRow: any, oldRow: any) => any>((row) => row,);
  const effectiveIdPath = idPath ?? ["Id"];
  processRowUpdateRef.current = (newRow, oldRow) => {
    const normalized = normalizeFlatToNested(newRow);
    const rowId = tableHelper.getRowIdFromPath(normalized, effectiveIdPath);
    const idx = changedRowsRef.current.findIndex((r) => tableHelper.getRowIdFromPath(r, effectiveIdPath) === rowId,);
    const next = [...changedRowsRef.current];
    if (idx >= 0) {
      next[idx] = normalized;
    } else {
      next.push(normalized);
    }
    changedRowsRef.current = next;
    setSetting(["TableChangedRows", tableName ?? "UnNamed"], [...changedRowsRef.current],);
    apiRef.current?.updateRows?.([normalized]);
    return normalized;
  };
  return processRowUpdateRef;
};

export const createRowClassName = (initialTableSettings: TableSettingsIdAndChanges,) => {
  return (params: any) => {
    const rowId = tableHelper.getRowIdFromPath(params.row, initialTableSettings?.idPath,);
    const isDeleted = initialTableSettings?.changedRowsRef?.current?.some(
      (row) => {
        const changedRowId = tableHelper.getRowIdFromPath(row, initialTableSettings?.idPath,);
        return changedRowId === rowId && row?.deleted;
      },
    );
    const base = params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
    return `${base} ${isDeleted ? "deleted-row" : ""}`.trim();
  };
};

export const createRowIdGetter = (initialTableSettings: { idPath?: string[]; }) => {
  return (row: any) => {
    const rowId = tableHelper.getRowIdFromPath(row, initialTableSettings?.idPath,);
    return rowId ?? row.id;
  };
};

export const useGroupExpansionState = ( apiRef: React.MutableRefObject<GridApiPremium | undefined>, tableExpanded: boolean | number | undefined, treeData?: boolean, rowsVersion?: unknown, ) => {
  // rowId -> expanded?
  const expansionStateRef = React.useRef<Record<string | number, boolean>>({});

  // Subscribe to expansion changes and keep the map in sync
  React.useEffect(() => {
    if (!treeData) return;
    const api = apiRef?.current;
    if (!api || typeof api.subscribeEvent !== "function") return;

    // If we don't have any saved expansion yet, but we *do* have a TableExpanded setting,
    // pre-seed it once for top-level groups so you keep your old "expand all" behaviour.
    if (
      Object.keys(expansionStateRef.current).length === 0 &&
      tableExpanded != null
    ) {
      try {
        const rootNode = api.getRowNode(GRID_ROOT_GROUP_ID) as any;
        const groups: any[] = rootNode?.children ?? [];
        groups.forEach((id: any) => { expansionStateRef.current[id] = !!tableExpanded; });
      } catch {
        // grid might not be ready yet
      }
    }
    const unsubscribe = api.subscribeEvent("rowExpansionChange", (node: any) => { expansionStateRef.current[node.id] = !!node.childrenExpanded; },);
    return unsubscribe;
  }, [apiRef, tableExpanded, treeData]);

  // NEW: whenever the row model changes (new row, commit, etc.), re-apply saved expansion
  React.useEffect(() => {
    if (!treeData) return;
    const api = apiRef?.current;
    if (!api) return;
    Object.entries(expansionStateRef.current).forEach(([id, expanded]) => {
      try {
        const node = api.getRowNode(id);
        if (node && node.type === "group") {
          api.setRowChildrenExpansion(id, expanded as boolean);
        }
      } catch {
        // ignore per-node failures
      }
    });
  }, [apiRef, treeData, rowsVersion]);

  // This is what we pass as isGroupExpandedByDefault to the grid (via tableSettings)
  const isGroupExpandedByDefault = React.useCallback(
    (node: any) => {
      // 1) Prefer the persisted expansion state if we have one for this node
      const saved = expansionStateRef.current[node.id];
      if (saved != null) return saved;

      // 2) Fallback to your previous TableExpanded logic
      if (typeof tableExpanded === "number") return tableExpanded > 1;
      return !!tableExpanded;
    },
    [tableExpanded],
  );
  return isGroupExpandedByDefault;
};

interface HasFilterAndApiRef {
  filterModel?: any;
  apiRef?: React.MutableRefObject<GridApiPremium | undefined>;
}

interface TableSettingsIdAndChanges {
  idPath?: string[];
  changedRowsRef?: React.MutableRefObject<any[]>;
}
